import { Box, Typography } from '@mui/material';
import { Check, X } from 'lucide-react';

/**
 * Password validator component that shows visual feedback on password requirements
 * @param {Object} props - Component props
 * @param {string} props.password - The password to validate
 * @returns {JSX.Element} Password validation component
 */
const PasswordValidator = ({ password }) => {
  // Password requirements
  const requirements = [
    {
      id: 'length',
      label: 'At least 8 characters long',
      validator: (pass) => pass.length >= 8,
    },
    {
      id: 'lowercase',
      label: 'Contains lowercase letter',
      validator: (pass) => /[a-z]/.test(pass),
    },
    {
      id: 'uppercase',
      label: 'Contains uppercase letter',
      validator: (pass) => /[A-Z]/.test(pass),
    },
    {
      id: 'number',
      label: 'Contains a number',
      validator: (pass) => /[0-9]/.test(pass),
    },
  ];

  // Password strength calculation
  const calculateStrength = (pass) => {
    if (!pass) return 0;

    const meetsRequirements = requirements.filter(req => req.validator(pass)).length;
    
    // Basic strength based on requirements met (0-100%)
    let strength = (meetsRequirements / requirements.length) * 100;
    
    // Bonus strength for length beyond minimum
    if (pass.length > 8) {
      strength += Math.min((pass.length - 8) * 2, 20); // Max 20% bonus for length
    }
    
    // Bonus for special characters
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pass)) {
      strength += 10;
    }
    
    // Cap at 100%
    return Math.min(strength, 100);
  };

  const strength = calculateStrength(password);
  
  // Determine color based on strength
  const getStrengthColor = () => {
    if (strength < 40) return '#f44336'; // Red
    if (strength < 70) return '#ff9800'; // Orange
    return '#4caf50'; // Green
  };

  const getStrengthLabel = () => {
    if (strength < 40) return 'Weak';
    if (strength < 70) return 'Moderate';
    return 'Strong';
  };

  if (!password) return null;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Password strength indicator */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Password strength: <span style={{ color: getStrengthColor(), fontWeight: 'bold' }}>{getStrengthLabel()}</span>
          </Typography>
          <Typography variant="body2" sx={{ color: getStrengthColor() }}>
            {Math.round(strength)}%
          </Typography>
        </Box>
        <Box sx={{ width: '100%', height: '4px', bgcolor: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${strength}%`,
              height: '100%',
              bgcolor: getStrengthColor(),
              transition: 'width 0.3s ease'
            }}
          />
        </Box>
      </Box>

      {/* Password requirements */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
        {requirements.map((req) => {
          const satisfied = req.validator(password);
          
          return (
            <Box 
              key={req.id} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: satisfied ? '#4caf50' : '#9e9e9e' 
              }}
            >
              {satisfied ? (
                <Check size={16} strokeWidth={2.5} />
              ) : (
                <X size={16} strokeWidth={2.5} color="#9e9e9e" />
              )}
              <Typography variant="caption" sx={{ ml: 1 }}>
                {req.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PasswordValidator;